import {
  NeoXInvocationBuilder,
  TNeedsApprovalArgs,
  TWithdrawNativeArgs,
  TWithdrawTokenArgs,
  TNeoXTransactionRequest,
} from '@/model/wallets/invocationsBuilder/NeoXInvocationBuilder'
import {ethers} from 'ethers'
import {neoXBridgeABI} from '@/libs/x-bridge/NeoXBridgeABI'
import {$} from '@/facade'
import {wallet} from '@cityofzion/neon-core'
import {EnvHelper} from '@/helpers/EnvHelper'
import {BridgeNetworkHelper} from '@/helpers/BridgeNetworkHelper'
import {BridgeFeeHelper} from '@/helpers/BridgeFeeHelper'

class NeoXNeonWalletInvocationBuilder implements NeoXInvocationBuilder {
  async needsApproval({
    tokenHash,
    amount,
    tokenDecimals,
  }: TNeedsApprovalArgs): Promise<boolean> {
    const provider = this.getProvider()

    const contract = new ethers.Contract(
      tokenHash,
      [
        'function allowance(address owner, address spender) view returns (uint256)',
      ],
      provider
    )

    const allowance: ethers.BigNumber = await contract.allowance(
      $.walletAdapter.xAddress!,
      BridgeNetworkHelper.xBridgeScriptHash()
    )

    const required = ethers.utils.parseUnits(amount, tokenDecimals)

    return allowance.lt(required)
  }

  approveInvocation({
    tokenHash,
    amount,
    tokenDecimals,
  }: TWithdrawTokenArgs): TNeoXTransactionRequest {
    const iface = new ethers.utils.Interface([
      'function approve(address spender, uint256 amount)',
    ])

    const data = iface.encodeFunctionData('approve', [
      BridgeNetworkHelper.xBridgeScriptHash(),
      ethers.utils.parseUnits(amount, tokenDecimals),
    ])

    return {
      data,
      from: $.walletAdapter.xAddress!,
      to: tokenHash,
    }
  }

  async withdrawTokenInvocation({
    tokenHash,
    amount,
    tokenDecimals,
  }: TWithdrawTokenArgs): Promise<TNeoXTransactionRequest> {
    const contract = new ethers.Contract(
      BridgeNetworkHelper.xBridgeScriptHash(),
      neoXBridgeABI
    )

    const parsedAmount = ethers.utils.parseUnits(amount, tokenDecimals)

    const to = `0x${wallet.getScriptHashFromAddress(
      $.walletAdapter.n3Address!
    )}`

    const data = contract.interface.encodeFunctionData('withdrawToken', [
      tokenHash,
      to,
      parsedAmount,
    ])

    return {
      data,
      from: $.walletAdapter.xAddress!,
      to: BridgeNetworkHelper.xBridgeScriptHash(),
      value: ethers.utils.hexValue(
        await BridgeFeeHelper.withdrawalFee(tokenHash)
      ),
    }
  }

  async withdrawNativeInvocation({
    amount,
  }: TWithdrawNativeArgs): Promise<TNeoXTransactionRequest> {
    const contract = new ethers.Contract(
      BridgeNetworkHelper.xBridgeScriptHash(),
      neoXBridgeABI
    )

    const decimals = EnvHelper.VUE_APP_DEFAULT_ETH_DECIMALS
    const parsedAmount = ethers.utils.parseUnits(amount, decimals)
    const bridgeFee = await BridgeFeeHelper.withdrawalFee()

    const to = `0x${wallet.getScriptHashFromAddress(
      $.walletAdapter.n3Address!
    )}`

    const data = contract.interface.encodeFunctionData('withdrawNative', [
      to,
      bridgeFee,
    ])

    return {
      data,
      from: $.walletAdapter.xAddress!,
      to: BridgeNetworkHelper.xBridgeScriptHash(),
      value: ethers.utils.hexValue(parsedAmount.add(bridgeFee)),
    }
  }

  /** Read-only provider on the Neo X network the bridge is configured for. */
  private getProvider() {
    return new ethers.providers.StaticJsonRpcProvider(
      BridgeNetworkHelper.xRpcUrl(),
      {
        chainId: BridgeNetworkHelper.xChainId(),
        name: 'neox',
      }
    )
  }
}

export const neoXNeonWalletInvocationBuilder = new NeoXNeonWalletInvocationBuilder()
